import { useEffect, useState, createContext, useContext, useCallback, useRef } from "react";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Link, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";

// Pages
import Landing from "./pages/Landing";
import Bars from "./pages/Bars";
import BarProfile from "./pages/BarProfile";
import Events from "./pages/Events";
import EventDetail from "./pages/EventDetail";
import MyEvents from "./pages/MyEvents";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import Admin from "./pages/Admin";
import ResetPassword from "./pages/ResetPassword";
import Privacy from "./pages/Privacy";
import CookiesPolicy from "./pages/CookiesPolicy";
import LegalNotice from "./pages/LegalNotice";
import Terms from "./pages/Terms";
import CheckEmail from "./pages/CheckEmail";
import VerifyEmail from "./pages/VerifyEmail";
import BandDashboard from "./pages/BandDashboard";
import AuthCallback from "./pages/AuthCallback";
import Settings from "./pages/Settings";
import UserDashboard from "./pages/UserDashboard";
import LandingBares from "./pages/LandingBares";
import LandingBandas from "./pages/LandingBandas";
import BarApprovalCallback from "./pages/BarApprovalCallback";
import PartnerDashboard from "./pages/PartnerDashboard";

// Components
import CookieBanner from "./components/CookieBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("token"));

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        });
        setUser(response.data);
      } catch (error) {
        localStorage.removeItem("token");
        setToken(null);
        setUser(null);
      }
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = (newToken, userData) => {
    // Clear any stale data first
    localStorage.removeItem("token");
    // Set new token
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setUser(userData);
    // Return true to indicate success (for navigation timing)
    return true;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {}
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, setUser, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, barOnly = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-primary text-xl font-mono">CARGANDO...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (barOnly && user.user_type !== "bar") {
    return <Navigate to="/" replace />;
  }

  return children;
};

// App Router
function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id (OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/bars" element={<Bars />} />
      <Route path="/bars/:barId" element={<BarProfile />} />
      <Route path="/events" element={<Events />} />
      <Route path="/events/:eventId" element={<EventDetail />} />
      <Route path="/my-events" element={<MyEvents />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/dashboard" element={
        <ProtectedRoute barOnly>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute>
          <Admin />
        </ProtectedRoute>
      } />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/privacidad" element={<Privacy />} />
      <Route path="/check-email" element={<CheckEmail />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/band-dashboard" element={<BandDashboard />} />
      <Route path="/user-dashboard" element={<UserDashboard />} />
      <Route path="/partner-dashboard" element={<PartnerDashboard />} />
      <Route path="/settings" element={<Settings />} />
      <Route path="/auth/google/callback" element={<AuthCallback />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/para-bares" element={<LandingBares />} />
      <Route path="/para-bandas" element={<LandingBandas />} />
      <Route path="/admin/approve-bar/:barId/:token" element={<BarApprovalCallback />} />
      <Route path="/admin/reject-bar/:barId/:token" element={<BarApprovalCallback />} />
      <Route path="/politica-cookies" element={<CookiesPolicy />} />
      <Route path="/aviso-legal" element={<LegalNotice />} />
      <Route path="/terminos" element={<Terms />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <CookieBanner />
        <Toaster 
          position="bottom-right" 
          toastOptions={{
            style: {
              background: '#111',
              border: '1px solid #333',
              color: '#ededed',
              borderRadius: '0',
              fontFamily: 'JetBrains Mono, monospace'
            }
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
